import React, { useCallback, useEffect, useMemo } from 'react';

import qs from 'qs';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useQueryParam, useResource } from 'react-resource-router';

import { Pagination } from 'src/components/pagination';
import PullRequestsTable, {
  PullRequestsTableSize,
  PullRequestsTableStateVisibility,
} from 'src/components/pull-requests-table';
import { SCROLL_TO_TOP_DURATION } from 'src/constants/permalink-scroll';
import { WORKSPACE_PULL_REQUESTS_LIST_LOAD, MetricSender } from 'src/metrics';
import scrollTo from 'src/redux/global/actions/scroll-to';
import { useLocation, useParams } from 'src/router/hooks';
import imgUrl from 'src/sections/repository/sections/pull-request-list/components/empty-state.svg';
import prMessages, {
  emptyResultsMessages,
} from 'src/sections/repository/sections/pull-request-list/components/i18n';
import EmptyResults from 'src/sections/workspace/components/empty-results';
import { Page } from 'src/types/pagination';
import urls from 'src/urls/workspaces';
import { getPagination } from 'src/utils/get-pagination';

import { useWorkspacePullRequests } from '../build-status-provider';
import { getWorkspacePullRequestsApi } from '../requests';
import { Query } from '../types';
import {
  parseQuery,
  workspacePullRequestsResource,
} from '../workspace-pull-requests-resource';

const PullRequestsPageTable = (): JSX.Element => {
  const [{ pullRequests, buildStatusesFetched }, { setPullRequests }] =
    useWorkspacePullRequests();

  const {
    data: pullRequestsData,
    loading: pullRequestsLoading,
    error: pullRequestsError,
  } = useResource(workspacePullRequestsResource);

  const showEmptyState = useMemo(
    () =>
      pullRequests?.length === 0 &&
      !(pullRequestsLoading || pullRequestsError) &&
      buildStatusesFetched,
    [pullRequests, pullRequestsLoading, pullRequestsError, buildStatusesFetched]
  );

  const showTableLoading = useMemo(
    () => pullRequestsLoading || !(buildStatusesFetched || pullRequests.length),
    [pullRequests, buildStatusesFetched, pullRequestsLoading]
  );

  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const { search, pathname } = useLocation();

  const dispatch = useDispatch();

  const { size, pagelen } = pullRequestsData || {};

  const api = useMemo(
    () =>
      getWorkspacePullRequestsApi(
        urls.api.internal.pullRequestBuildStatuses(workspaceSlug)
      ),
    [workspaceSlug]
  );

  useEffect(() => {
    if (pullRequestsData?.pullRequests) {
      setPullRequests(api, pullRequestsData.pullRequests);
    }
  }, [pullRequestsData, setPullRequests, api]);

  const [page] = useQueryParam('page');

  const [state] = useQueryParam('state');

  const showState =
    state?.split('+').length !== 1 || state === 'ALL'
      ? PullRequestsTableStateVisibility.All
      : PullRequestsTableStateVisibility.None;
  const queryParams = useMemo(
    () => qs.parse(search, { ignoreQueryPrefix: true }),
    [search]
  );
  const filter = useMemo(() => parseQuery(queryParams as Query), [queryParams]);

  const filterApplied =
    !!(filter.prState.length >= 1 && filter.prState[0] !== 'ALL') ||
    !!filter.search ||
    !!filter.authorsAaid ||
    !!filter.project ||
    filter.prUserFilter !== 'REVIEWING';

  const getPaginationUrl = useCallback(
    (selectedPage: number) => {
      const newQueryString = qs.stringify({
        ...queryParams,
        page: selectedPage,
      });
      return `${pathname}?${newQueryString}`;
    },
    [pathname, queryParams]
  );

  const getPages = useMemo(() => {
    return size && pagelen ? Math.ceil(size / pagelen) : 0;
  }, [size, pagelen]);

  const onPageChange = useCallback(
    () =>
      dispatch(
        scrollTo({
          targetId: 'root',
          duration: SCROLL_TO_TOP_DURATION,
        })
      ),
    [dispatch]
  );

  const renderPagination = () => {
    const pages: Page[] = getPagination(getPages)(getPaginationUrl);
    const parsedPage = page ? parseInt(page, 10) : 1;
    return (
      <Pagination
        dataTestId="workspace-pagination"
        isError={!!pullRequestsError}
        isLoading={showTableLoading}
        onPageChange={onPageChange}
        page={!isNaN(parsedPage) ? Math.max(parsedPage, 1) : 1}
        pages={pages}
        uiEventSource="WorkspacePullRequestScreen"
        uiEventSubjectId="WorkspacePullRequestPagination"
      />
    );
  };

  // Empty states:
  // 1) Current filter does not match any pull requests or
  // 2) User does not have access to any pull requests
  const renderEmptyState = () => {
    const emptyStateProps = filterApplied
      ? {
          header: emptyResultsMessages.noMatches.emptyResultsHeading,
          desc: emptyResultsMessages.noMatches.emptyResultsInfo,
          primaryAction: {
            href: window.location.pathname,
            msg: emptyResultsMessages.noMatches.clearAllFilters,
          },
        }
      : {
          header: emptyResultsMessages.noOpen.emptyResultsHeading,
          desc: emptyResultsMessages.noOpen.emptyResultsInfo,
          imageUrl: imgUrl,
        };

    return <EmptyResults {...emptyStateProps} />;
  };

  return (
    <>
      {showEmptyState ? (
        renderEmptyState()
      ) : (
        <PullRequestsTable
          pullRequests={pullRequests}
          Heading={<FormattedMessage {...prMessages.listHeader} />}
          renderPagination={renderPagination}
          isLoading={showTableLoading}
          isError={!!pullRequestsError}
          showRepository
          showAvatar
          showState={showState}
          size={PullRequestsTableSize.Large}
        />
      )}
      {!pullRequestsLoading && (
        <MetricSender metric={WORKSPACE_PULL_REQUESTS_LIST_LOAD} />
      )}
    </>
  );
};

export default PullRequestsPageTable;
